import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { StepFrame, Callout } from '../StepFrame';
import { YesNo } from '../../ui/YesNo';
import { HelpPanel } from '../../ui/HelpPanel';
import { usePortal } from '../../../contexts/PortalContext';
import { declarationGroups } from '../../../data/declarations';
import { useTokens } from '../../../hooks/useTokens';
export function DeclarationGroupStep({
  stepId
}: {
  stepId: string;
}) {
  const {
    answers,
    update
  } = usePortal();
  const {
    fill
  } = useTokens();
  const group = declarationGroups.find(g => g.stepId === stepId);
  if (!group) return null;
  const answered = group.questions.filter(q => answers.declarations[q.id]).length;
  return <StepFrame title={group.title} description={fill(group.intro)} help={<HelpPanel question="Why are these questions so formal?" data-id="element-221" data-mp="1mfldixlmpxrt">
          This wording comes directly from the federal Uniform Residential Loan Application, so we can’t change it. We’ve added a plain-language note under each one to help.
        </HelpPanel>} data-id="element-220" data-mp="oycwhuyeyl83">
      <div className="mb-4 flex items-center gap-3 text-sm text-ink-500" data-id="element-222" data-mp="275u6gwukb2o1">
        <div className="h-1 w-24 overflow-hidden rounded-full bg-ink-200" data-id="element-223" data-mp="1sgxvos14fbjq">
          <motion.div className="h-full bg-pulse-600" animate={{
          width: `${answered / group.questions.length * 100}%`
        }} transition={{
          duration: 0.3
        }} data-id="element-224" data-mp="1setvak2fkuo7" />
        </div>
        <span className="tabular" data-id="element-225" data-mp="1jeex4k4dbl3a">
          {answered} of {group.questions.length} answered
        </span>
      </div>
      <ol className="divide-y divide-ink-100 rounded-2xl bg-white ring-1 ring-ink-200" data-id="element-226" data-mp="1nfgx5364016b">
        {group.questions.map(q => {
        const v = answers.declarations[q.id] ?? '';
        return <li key={q.id} className="p-4 md:p-5" data-id="element-227" data-mp="1eizms25kk7bm">
              <div className="flex gap-3" data-id="element-228" data-mp="33wuynk5snvzy">
                <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full transition-colors duration-150 ${v ? 'bg-positive-500 text-white' : 'bg-ink-100 text-ink-400'}`} aria-hidden data-id="element-229" data-mp="33tqlhxacppx5">
                  {v ? <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-230" data-mp="37beehv746s79" /> : <span className="text-[10px] font-semibold" data-id="element-231" data-mp="32dmoj6q15g5p">{q.id}</span>}
                </span>
                <div className="flex-1" data-id="element-232" data-mp="2xgmhotlcw9dj">
                  <p className="text-[15px] leading-snug text-ink-900" data-id="element-233" data-mp="2xhebpzzb1z1p">{q.question}</p>
                  {q.plain && <p className="mt-1.5 text-sm leading-snug text-ink-500" data-id="element-234" data-mp="2siunfsx8ob66">{q.plain}</p>}
                  <div className="mt-3" data-id="element-235" data-mp="3q6bh52if41d1">
                    <YesNo label={q.question} value={v} onChange={nv => update({
                  declarations: {
                    ...answers.declarations,
                    [q.id]: nv
                  }
                })} data-id="element-236" data-mp="3l4m4xjjglhnd" />
                  </div>
                </div>
              </div>
            </li>;
      })}
      </ol>
    </StepFrame>;
}
const ethnicityOptions = ['Hispanic or Latino', 'Not Hispanic or Latino'];
const raceOptions = ['American Indian or Alaska Native', 'Asian', 'Black or African American', 'Native Hawaiian or Other Pacific Islander', 'White'];
const sexOptions = ['Female', 'Male'];
const DECLINE = 'I do not wish to provide this information';
function ChipGroup({
  legend,
  options,
  selected,
  onToggle
}: {
  legend: string;
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
}) {
  return <fieldset className="mt-8 first:mt-0" data-id="element-237" data-mp="pa0yu15wipp5">
      <legend className="mb-3 text-sm font-medium text-ink-700" data-id="element-238" data-mp="u7ay08j3efq6">{legend}</legend>
      <div className="flex flex-wrap gap-2" data-id="element-239" data-mp="z6drcvppmxib">
        {[...options, DECLINE].map(o => {
        const on = selected.includes(o);
        return <button key={o} aria-pressed={on} onClick={() => onToggle(o)} className={`min-h-10 rounded-full px-4 py-2 text-left text-sm font-medium ring-1 ring-inset transition-[background-color,color,box-shadow] duration-150 ${on ? 'bg-ink-900 text-white ring-ink-900' : o === DECLINE ? 'bg-transparent text-ink-500 ring-ink-200 hover:ring-ink-300' : 'bg-white text-ink-700 ring-ink-200 hover:ring-ink-300'}`} data-id="element-240" data-mp="135wfe0ctvopd">
              {o}
            </button>;
      })}
      </div>
    </fieldset>;
}
export function DemographicsStep() {
  const {
    answers,
    update
  } = usePortal();
  const d = answers.demographics;
  const toggle = (list: string[], v: string, multi: boolean) => {
    if (v === DECLINE) return list.includes(DECLINE) ? [] : [DECLINE];
    const base = list.filter(x => x !== DECLINE);
    if (!multi) return base.includes(v) ? [] : [v];
    return base.includes(v) ? base.filter(x => x !== v) : [...base, v];
  };
  return <StepFrame title="Demographic information" description="This is optional. The federal government asks lenders to collect it to make sure all borrowers are treated fairly." help={<HelpPanel question="Will this affect my application?" data-id="element-242" data-mp="3l3b3kigrv5t3">
          No. Your answers have no effect on whether you’re approved or the rate you receive. You may select more than one ethnicity or race, or choose not to answer.
        </HelpPanel>} data-id="element-241" data-mp="29pfeagw5lgo5">
      <Callout data-id="element-243" data-mp="3ooctva7cdr3s">Optional — you can skip this entire page.</Callout>
      <div className="mt-8" data-id="element-244" data-mp="3tmo6fsp0ufpp">
        <ChipGroup legend="Ethnicity" options={ethnicityOptions} selected={d.ethnicity} onToggle={v => update({
        demographics: {
          ...d,
          ethnicity: toggle(d.ethnicity, v, true)
        }
      })} data-id="element-245" data-mp="3tiqk6y75d81l" />
        <ChipGroup legend="Race" options={raceOptions} selected={d.race} onToggle={v => update({
        demographics: {
          ...d,
          race: toggle(d.race, v, true)
        }
      })} data-id="element-246" data-mp="3jn5kj6pjwjv0" />
        <ChipGroup legend="Sex" options={sexOptions} selected={d.sex ? [d.sex] : []} onToggle={v => update({
        demographics: {
          ...d,
          sex: toggle(d.sex ? [d.sex] : [], v, false)[0] ?? ''
        }
      })} data-id="element-247" data-mp="g6ghvvt39uam" />
      </div>
    </StepFrame>;
}